import { Briefcase, ShoppingBag, Stethoscope, Scissors, UtensilsCrossed, Building2, GraduationCap, MousePointerClick, RefreshCcw, Wrench, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { waLink } from "@/lib/site";

const SERVICES = [
  { icon: Briefcase, t: "Business Website Designing", d: "Professional and attractive websites for startups, entrepreneurs, and established businesses.", img: "/images/corporate.jpg", span: "md:col-span-2 lg:col-span-2 lg:row-span-2", big: true },
  { icon: ShoppingBag, t: "E-Commerce Website Development", d: "Modern online shopping websites featuring product catalogues, shopping carts, and payment gateway integration.", img: "/images/ecommerce.jpg", span: "md:col-span-2 lg:col-span-2", wide: true },
  { icon: Stethoscope, t: "Clinic & Hospital Websites", d: "Professional websites for doctors, clinics, hospitals, dentists, and healthcare providers, with appointment enquiry features." },
  { icon: Scissors, t: "Salon & Beauty Parlour Websites", d: "Beautiful websites for salons, spas, beauty parlours, and aesthetic clinics." },
  { icon: UtensilsCrossed, t: "Restaurant & Cafe Websites", d: "Attractive restaurant websites featuring digital menus, reservation enquiries, and online ordering integration where required." },
  { icon: Building2, t: "Real Estate Websites", d: "Professional property listing websites for builders, brokers, and property consultants." },
  { icon: GraduationCap, t: "Educational Institute Websites", d: "Modern websites for schools, colleges, coaching centres, and training institutes." },
  { icon: MousePointerClick, t: "Landing Page Designing", d: "High-converting landing pages for advertisements, promotions, and lead generation." },
  { icon: RefreshCcw, t: "Website Redesigning", d: "Transform outdated websites into modern, professional, responsive websites.", span: "md:col-span-2 lg:col-span-2" },
  { icon: Wrench, t: "Website Maintenance", d: "Website updates, security improvements, content management, and technical support.", span: "md:col-span-2 lg:col-span-2" },
];

const track = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const ServiceCard = ({ s, i }) => (
  <article
    onMouseMove={track}
    data-testid={`service-card-${i}`}
    className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/[0.08] bg-navy/70 p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold/35 ${s.big ? "min-h-[420px] lg:min-h-full" : ""} ${s.wide ? "min-h-[260px]" : ""}`}
  >
    {s.img && (
      <>
        <img src={s.img} alt={`${s.t} example visual`} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-ink/75 to-ink/10" />
      </>
    )}
    <div className="spotlight pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    <div className="relative flex items-start justify-between">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-[var(--gold)] transition-transform duration-500 group-hover:rotate-[-8deg] group-hover:scale-110">
        <s.icon className="h-5 w-5" />
      </span>
      <span className="font-display text-sm text-slate-400">{String(i + 1).padStart(2, "0")}</span>
    </div>
    <div className="relative mt-auto pt-10">
      <h3 className={`font-display font-semibold leading-tight text-white ${s.big ? "text-2xl sm:text-3xl" : "text-lg"}`}>{s.t}</h3>
      <p className="mt-3 text-sm leading-relaxed text-slate-300">{s.d}</p>
      <a
        href={waLink(`Hello Pratap Group, I am interested in ${s.t}. Please share details and pricing.`)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`service-quote-button-${i}`}
        className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white transition-[background-color,color,border-color] duration-300 hover:border-[var(--gold)] hover:bg-[var(--gold)] hover:text-black"
      >
        Get a Quote <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    </div>
  </article>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <SectionHead eyebrow="Our Services" title={<>Websites crafted for <span className="text-royal-grad italic">every kind</span> of business.</>} />
        <Reveal delay={0.2}><p className="max-w-sm text-sm leading-relaxed text-[var(--muted)]">Pick the website your business needs — tap “Get a Quote” on any card to chat with us instantly on WhatsApp.</p></Reveal>
      </div>
      <div className="mt-16 grid auto-rows-[minmax(250px,auto)] grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <Reveal key={s.t} delay={(i % 4) * 0.06} className={s.span || ""}>
            <ServiceCard s={s} i={i} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
